import { Button } from "@component/button";
import { Input } from "@component/input";
import { generateUserReferral, getUserReferral } from "@service/referral";
import { getCookie } from "@util/cookie";
import { convertCurrency } from "@util/currency";
import { trackEvent } from "@util/mixpanel";
import { getSubdomainHost } from "@util/name";
import { catchErrorLog } from "@util/sentry";
import { useQuery } from "@util/useQuery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
    companySubdomain: string;
    jobLink: string;
    jobTitle: string;
    isHaveReferralLink: boolean;
    referralCode: string;
    referralAmount: number;
    currency: string;
    onCompleted: () => void;
}

export default function ReferralNote(props: Props) {
    const token = getCookie("authToken");
    const navigate = useNavigate();
    const queryParams = useQuery();
    const [copied, setCopied] = useState(false);
    const [isHaveReferralLink, setIsHaveReferralLink] = useState<boolean>(false);
    const [referralCode, setReferralCode] = useState<string>("");
    const [isGeneratingReferralLink, setIsGeneratingReferralLink] = useState<boolean>(false);
    const referrallink = `${getSubdomainHost(props.companySubdomain)}${props.jobLink}?referral=${referralCode}`;

    useEffect(() => {
        setIsHaveReferralLink(props.isHaveReferralLink);
        setReferralCode(props.referralCode);
    }, [props.isHaveReferralLink, props.referralCode]);

    const handleCopy = () => {
        trackEvent("job_referral_link_copied_boxout", {
            job_title: props.jobTitle || "",
        })
        navigator.clipboard.writeText(referrallink).then(() => {
            setCopied(true);
            trackEvent("job_referral_link_copied", {
                job_title: props.jobTitle || "",
            })
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const generateReferralLink = () => {
        if (!token) {
            // redirect to login page if user is not logged in
            navigate("/", { state: { from: window.location.pathname } });
            return;
        }

        setIsGeneratingReferralLink(true);
        generateUserReferral().then((response) => {
            if (response && response.data.code === 200) {
                setIsHaveReferralLink(true);
                setReferralCode(response.data.data.referral_code);
                trackEvent("job_referral_activate", {
                    "value": true,
                })
                if (queryParams.get("referral")) {
                    navigate(props.jobLink, { replace: true });
                    props.onCompleted();
                }
            }
        }).catch((error) => {
            if (error.response && error.response.data.code === 400) {
                return getUserReferral().then((response) => {
                    if (response && response.data.code === 200) {
                        setIsHaveReferralLink(true);
                        setReferralCode(response.data.data.referral_code);
                    }
                })
            }
            trackEvent("job_referral_activate", {
                "value": false,
            })
            catchErrorLog(`Error generating referral link: ${error}`);
        }).catch((error) => {
            catchErrorLog(`Error get referral link: ${error}`);
        }).finally(() => {
            setIsGeneratingReferralLink(false);
        })
    }

    return (
        <div className="space-y-4 mt-6">
            <div className="pt-4">
                <h3 className="font-semibold text-lg mb-2">Earn {convertCurrency(props.referralAmount, props.currency)} by referring</h3>
                <p className="text-sm text-gray-600 mb-2">
                    Share the referral link below, and{" "}
                    <span className="font-medium">
                        earn {convertCurrency(props.referralAmount, props.currency)} for each successful referral
                    </span>{" "}
                    through this unique link.<br />There's no limit on how many people you can
                    refer. Restrictions may apply.
                    <a href="#" className="text-blue-600 hover:underline ml-1">
                        Learn more
                    </a>
                </p>
                {isHaveReferralLink ? (
                    <div className="flex space-x-2 mt-3">
                        <div className="relative flex-1">
                            <Input
                                value={referrallink}
                                readOnly
                                className="text-sm text-blue-600"
                            />
                        </div>
                        <Button
                            onClick={handleCopy}
                            variant="outline"
                            className="shadow-none"
                        >
                            {copied ? "Copied!" : "Copy"}
                        </Button>
                    </div>
                ) : (
                    <div className="flex space-x-2 mt-6">
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={generateReferralLink}
                            disabled={isGeneratingReferralLink}
                            className="w-full rounded-full shadow-none text-base"
                        >
                            Activate my referral link
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
